import {
  EventsStatuses,
  FilterKeys,
  GetEventsQueryFilters,
  RepetitionType,
  SchoolYear,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import {
  AgeGroupSelectMultiple,
  DropdownSelectFilter,
  EventRecurrenceSelect,
  EventsParticipationOptions,
  EventsStatusOptions,
  FiltersSidebar,
  FiltersSidebarMethods,
  getEventsStatusLabel,
  getParticipantLabel,
  GroupSelectMultiple,
  PropertyTypeSelectMultiple,
  SchoolPeriodsRangeSelect,
  SchoolPeriodsRangeSelectComponent,
  toggleMultipleValueArrayProperty,
  useFiltersSidebar,
} from '@schooly/components/filters';
import { EventsInvite, SchoolPropertyType, SchoolUserRole } from '@schooly/constants';
import { forwardRef, useCallback, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useSchool } from '../../hooks/useSchool';
import { useEventsFilters } from './useEventsFilters';

type EventsFiltersSidebarProps = {
  filters: GetEventsQueryFilters;
  defaultFilters: GetEventsQueryFilters;
  schoolId: string;
  onApply: (v: GetEventsQueryFilters) => void;
  defaultSchoolYear?: SchoolYear;
};

export const EventsFiltersSidebar = forwardRef<FiltersSidebarMethods, EventsFiltersSidebarProps>(
  ({ filters: propsFilters, defaultFilters, onApply, schoolId, defaultSchoolYear }, ref) => {
    const [filters, setFilters] = useState(propsFilters);
    const { formatMessage } = useIntl();
    const { permissions } = useAuth();
    const { hasHouses } = useSchool();
    const { allowClose, preventClose, sidebar } = useFiltersSidebar(ref);
    const isGroupViewer = permissions.includes('group_viewer');
    const periodsRef = useRef<SchoolPeriodsRangeSelectComponent>(null);

    const {
      labels,
      filtersEventStatus,
      filtersInviteType,
      filtersAgeGroup,
      filtersHouse,
      filtersGroup,
      onSetDate,
      onSetEventStatus,
      onClearEventStatus,
      onSetInviteType,
      onClearInviteType,
      onSetAgeGroup,
      onClearAgeGroup,
      onSetHouse,
      onClearHouse,
      onSetGroup,
      onClearGroup,
    } = useEventsFilters({ filters, onSetFilters: setFilters, schoolId });

    const onSelectEventStatus = useCallback(
      (status: EventsStatuses) => {
        onSetEventStatus(toggleMultipleValueArrayProperty(filtersEventStatus, [status]));
      },
      [filtersEventStatus, onSetEventStatus],
    );

    const onSelectInviteType = useCallback(
      (inviteType: EventsInvite) => {
        onSetInviteType(toggleMultipleValueArrayProperty(filtersInviteType, [inviteType]));
      },
      [filtersInviteType, onSetInviteType],
    );

    const onSelectAgeGroup = useCallback(
      (ageGroups: string[]) => {
        onSetAgeGroup(toggleMultipleValueArrayProperty(filtersAgeGroup, ageGroups));
      },
      [filtersAgeGroup, onSetAgeGroup],
    );

    const onSelectHouse = useCallback(
      (houseId: string) => {
        onSetHouse(toggleMultipleValueArrayProperty(filtersHouse, [houseId]));
      },
      [filtersHouse, onSetHouse],
    );

    const onSelectGroup = useCallback(
      (groupId: string) => {
        onSetGroup(toggleMultipleValueArrayProperty(filtersGroup, [groupId]));
      },
      [filtersGroup, onSetGroup],
    );

    const handleApply = useCallback(() => {
      onApply(filters);
    }, [filters, onApply]);

    const handleClear = useCallback(() => {
      setFilters(defaultFilters);
      if (defaultFilters.date) {
        periodsRef.current?.updateDates(defaultFilters.date);
      }
    }, [defaultFilters]);

    const intersectDateFilter = useMemo(
      () => ({ [FilterKeys.IntersectDate]: filters.date }),
      [filters.date],
    );

    return (
      <FiltersSidebar
        onOpen={() => {
          setFilters(propsFilters);
        }}
        ref={sidebar}
        title={formatMessage(
          { id: 'filter-FiltersForPage' },
          { page: formatMessage({ id: 'section-Events' }) },
        )}
        onClear={handleClear}
        onApply={handleApply}
      >
        <SchoolPeriodsRangeSelect
          ref={periodsRef}
          showSelectedPeriodLabel={false}
          schoolId={schoolId}
          defaultSchoolYear={defaultSchoolYear}
          hasManagePermission={permissions.includes('school_admin')}
          onSetDate={onSetDate}
          placeholder={labels[FilterKeys.Date]}
          date={filters.date || []}
          requiredLabel="required"
          onOpen={preventClose}
          onClose={allowClose}
          schoolYearFromToday
        />

        <DropdownSelectFilter
          placeholder={labels[FilterKeys.EventStatus]}
          onSelect={onSelectEventStatus}
          selected={filtersEventStatus || []}
          options={EventsStatusOptions}
          getLabel={(status) => formatMessage({ id: getEventsStatusLabel(status) })}
          onClear={onClearEventStatus}
          onOpen={preventClose}
          onClose={allowClose}
        />

        <DropdownSelectFilter
          placeholder={labels[FilterKeys.InviteType]}
          onSelect={onSelectInviteType}
          selected={filtersInviteType || []}
          options={EventsParticipationOptions}
          getLabel={(inviteType) => formatMessage({ id: getParticipantLabel(inviteType) })}
          onClear={onClearInviteType}
          onOpen={preventClose}
          onClose={allowClose}
        />

        <AgeGroupSelectMultiple
          allowArchived
          userRole={SchoolUserRole.Student}
          placeholder={labels[FilterKeys.AgeGroup]}
          schoolId={schoolId}
          onSelect={onSelectAgeGroup}
          selectedIds={filtersAgeGroup || []}
          onClear={onClearAgeGroup}
          onOpen={preventClose}
          onClose={allowClose}
        />

        {hasHouses && (
          <PropertyTypeSelectMultiple
            placeholder={labels[FilterKeys.House]}
            allowArchived
            userRole={SchoolUserRole.Student}
            schoolId={schoolId}
            onSelectId={onSelectHouse}
            selectedIds={filtersHouse || []}
            onClear={onClearHouse}
            propertyType={SchoolPropertyType.House}
            onOpen={preventClose}
            onClose={allowClose}
          />
        )}
        {isGroupViewer && (
          <GroupSelectMultiple
            placeholder={labels[FilterKeys.Group]}
            schoolId={schoolId}
            onSelectGroupId={onSelectGroup}
            selectedIds={filtersGroup || []}
            onClear={onClearGroup}
            filters={intersectDateFilter}
            onOpen={preventClose}
            onClose={allowClose}
          />
        )}
        <EventRecurrenceSelect
          placeholder={formatMessage({
            id: 'events-recurring',
          })}
          onClear={() => setFilters((s) => ({ ...s, [FilterKeys.RecurrenceId]: undefined }))}
          schoolId={schoolId}
          onSelectRecurrence={(v) =>
            setFilters((s) => ({
              ...s,
              [FilterKeys.RecurrenceId]: [v],
            }))
          }
          selectedId={filters[FilterKeys.RecurrenceId]?.[0]}
          filters={{ [FilterKeys.RepetitionType]: [RepetitionType.FirstOfRecurrence] }}
          onOpen={preventClose}
          onClose={allowClose}
        />
      </FiltersSidebar>
    );
  },
);
